import React from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Archive, Trash2 } from "lucide-react";

/**
 * Danger tab — two irreversible actions gated by typing the channel name.
 * `confirmMode` is `"archive" | "delete" | null` — `null` shows the two
 * launcher cards, non-null shows the confirmation form.
 */
export default function DangerTab({
  channelName,
  confirmMode, setConfirmMode,
  confirmName, setConfirmName,
  confirmBusy, onExecute,
}) {
  if (!confirmMode) {
    return (
      <div className="space-y-3 pt-1" data-testid="channel-settings-danger">
        <div className="border border-amber-300 bg-amber-50 rounded-lg p-3">
          <div className="flex items-start gap-2">
            <Archive className="w-4 h-4 text-amber-700 mt-0.5"/>
            <div className="flex-1">
              <div className="text-sm font-semibold text-zinc-900">Archive channel</div>
              <p className="text-xs text-zinc-600 mt-0.5">
                Hides the channel from every sidebar. Message history is kept and can be restored later by an admin.
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setConfirmMode("archive")}
              data-testid="channel-danger-archive"
            >
              Archive
            </Button>
          </div>
        </div>
        <div className="border border-red-300 bg-red-50 rounded-lg p-3">
          <div className="flex items-start gap-2">
            <Trash2 className="w-4 h-4 text-red-700 mt-0.5"/>
            <div className="flex-1">
              <div className="text-sm font-semibold text-zinc-900">Delete permanently</div>
              <p className="text-xs text-zinc-600 mt-0.5">
                Erases the channel and every message inside it. This cannot be undone.
              </p>
            </div>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => setConfirmMode("delete")}
              data-testid="channel-danger-delete"
            >
              Delete
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const destructive = confirmMode === "delete";
  return (
    <div className="space-y-3 pt-1" data-testid="channel-settings-danger">
      <div className={`border rounded-lg p-3 ${destructive ? "border-red-400 bg-red-50" : "border-amber-400 bg-amber-50"}`}>
        <div className="text-sm font-semibold text-zinc-900">
          {destructive ? "Confirm permanent deletion" : "Confirm archive"}
        </div>
        <p className="text-xs text-zinc-700 mt-1">
          Type <span className="font-mono font-semibold">{channelName}</span> to confirm.
        </p>
        <Input
          value={confirmName}
          onChange={(e) => setConfirmName(e.target.value)}
          placeholder={channelName}
          className="mt-2 bg-white"
          data-testid="channel-danger-confirm-input"
          autoFocus
        />
        <div className="flex justify-end gap-2 mt-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { setConfirmMode(null); setConfirmName(""); }}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            variant={destructive ? "destructive" : "default"}
            disabled={confirmBusy || confirmName !== channelName}
            onClick={onExecute}
            data-testid="channel-danger-confirm-execute"
          >
            {confirmBusy ? "Working…" : (destructive ? "Delete forever" : "Archive channel")}
          </Button>
        </div>
      </div>
    </div>
  );
}
